import { ExecutionContext } from "./types/ExecutionContext";
import { TrackingManager } from "./TrackingManager";
import {
	TrackedEventData,
	GaEventData,
	InternalEventData,
} from "./types/EventsData";

/**
 * Initializes Google Analytics, Bing and internal tracking.
 *
 * @param executionContext An execution context to use for tracking.
 * @returns A promise.
 */
export function initializeTracking(
	executionContext: ExecutionContext
): Promise<void> {
	return TrackingManager.getInstance().initialize(executionContext);
}
/**
 * Submits an event to both Google Analytics(GA) and our internal event tracking specifying webuserDataId as a component,
 * an action as "share" and filling other attributes. It uses the submitTrackedEvent under the hood.
 *
 * @param webuserDataId The webuserDataId to use as an action attribute.
 */
export function trackSharingEvent(webuserDataId: string): void {
	TrackingManager.getInstance().trackSharingEvent(webuserDataId);
}
/**
 * Submits an event to both Google Analytics(GA) and our internal event tracking specifying webuserDataId as a component,
 * an action as "signed" and filling other attributes. It uses the submitTrackedEvent under the hood.
 *
 * @param webuserDataId The webuserDataId to use as an action attribute.
 */
export function trackSignedEvent(webuserDataId: string): void {
	TrackingManager.getInstance().trackSignedEvent(webuserDataId);
}
/**
 * Submits events to both Google Analytics(GA) and our internal event tracking.
 *
 * http://internalwiki.lawdepot.com/index.php?title=Event_Tracking_Technical_Documentation
 *
 * Notes:
 * You can call submitInternalEvent instead of this function if you only want to submit the event internally.
 * For example to avoid sending too much data to Google Analytics
 * Experiment data is automatically included(pulled from dcsExp cookie)
 * The page name is always included in internal event tracking
 *
 * @param eventData The data to submit tracked event.
 */
export function submitTrackedEvent(eventData: TrackedEventData): void {
	TrackingManager.getInstance().submitTrackedEvent(eventData);
}
/**
 * Submits an event to our internal event tracking.
 *
 * http://internalwiki.lawdepot.com/index.php?title=Event_Tracking_Technical_Documentation
 *
 * Notes:
 * You can call submitTrackedEvent instead of this function if you want to submit this event internally and to GA
 * Experiment data is automatically included(pulled from dcsExp cookie)
 * The page name is always included in internal event tracking
 *
 * @param eventData The data to submit an internal event.
 */
export function submitInternalEvent(eventData: InternalEventData): void {
	TrackingManager.getInstance().submitInternalEvent(eventData);
}
/**
 * Submits an event to Google Analytics.
 *
 * @param eventData The data to submit a Google Analytics event.
 */
export function submitGAEvent(eventData: GaEventData): void {
	TrackingManager.getInstance().submitGAEvent(eventData);
}

/**
 * Tracks contract groups/sections.
 *
 * @param newSection The section to track.
 * @param newGroup The group to track.
 */
export function trackContractSection(
	newSection: string,
	newGroup: string
): void {
	TrackingManager.getInstance().trackContractSection(newSection, newGroup);
}
